// ─── Il guscio: chi è entrato, dove può andare, e la porta per uscire ────────

import { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Permesso } from '@pmo/shared';
import { esci } from './lib/api.js';
import { bollinoDelCapitolo, apriIlCapitoloDellaPagina, capitoloAperto } from './features/menu/capitoli.js';
import { RichiediPermesso, useSessione } from './features/auth/sessione.js';
import { Accesso } from './routes/accesso/Accesso.js';
import { CambioPassword } from './routes/accesso/CambioPassword.js';
import { Riapri } from './routes/accesso/Riapri.js';
import { Calendario } from './routes/calendario/Calendario.js';
import { usaDaSegnare } from './features/calendario/DaSegnare.js';
import { Soci } from './routes/soci/Soci.js';
import { Cassa } from './routes/cassa/Cassa.js';
import { Utenti } from './routes/impostazioni/Utenti.js';
import { Registro } from './routes/impostazioni/Registro.js';
import { Chiavi } from './routes/impostazioni/Chiavi.js';
import { Campi } from './routes/impostazioni/Campi.js';
import { Fasce } from './routes/impostazioni/Fasce.js';
import { Chiusure } from './routes/impostazioni/Chiusure.js';
import { Listino } from './routes/impostazioni/Listino.js';
import { Listini } from './routes/impostazioni/Listini.js';
import { ListinoLezioni } from './routes/impostazioni/ListinoLezioni.js';
import { Maestri } from './routes/impostazioni/Maestri.js';
import { Rubrica } from './routes/impostazioni/Rubrica.js';
import { Avvisi, usaNonConsegnatiOggi } from './routes/impostazioni/Avvisi.js';
import { Suggerimenti, usaDaLeggere } from './routes/impostazioni/Suggerimenti.js';
import { BotSoci } from './routes/impostazioni/BotSoci.js';
import { Documenti } from './routes/impostazioni/Documenti.js';
import { BorsellinoAccontoCrediti } from './routes/impostazioni/Borsellino.js';
import { MioAccount } from './routes/io/MioAccount.js';
import { AreaMaestro } from './routes/maestro/AreaMaestro.js';
import { usaVersione } from './features/versione.js';

interface Voce {
  dove: string;
  testo: string;
  /** Assente = la vedono tutti: non c'è niente da permettere. */
  permesso?: Permesso;
  /**
   * Accanto al nome, un bollino rosso: 💡 i suggerimenti da leggere, 🔔 gli
   * avvisi di oggi non consegnati.
   */
  conta?: 'suggerimenti' | 'avvisi' | 'da-segnare';
}

/**
 * ⭐⭐ IL MENU STA A SINISTRA, E RAGGRUPPATO — 16/09/2026, richiesta sua.
 *
 * 🗣️ *«ragioni se ci conviene mettere il menu che sta in alto a sinistra in
 * maniera da sfruttare l'altezza di una pagina, visto che un PC è un monitor
 * sempre più largo che alto?»*
 *
 * 📏 Misurato prima di spostarlo, perché la ragione è un numero e non un gusto:
 * in alto la testata e il menu costavano **128 px** di altezza — e a questa
 * larghezza il menu andava **a capo su due righe**, cioè spendeva il doppio
 * proprio quando c'era meno spazio. Sul calendario quei 128 px valgono quattro
 * ore di giornata, o — che è la cosa che si vede — **i nomi dei giocatori dentro
 * i blocchi**: ricompaiono sopra i 39 px per ora, soglia che un portatile da
 * 13" supera col menu a sinistra e non lo supera col menu in alto.
 *
 * ⚖️ E la larghezza che costa non è larghezza perduta: le pagine di testo erano
 * già limitate a 72rem, quindi su un monitor da 1920 i 196 px del menu escono
 * dal margine bianco che c'era prima. ⇒ Sotto i 1200 px invece la barra **si
 * chiude**, perché lì il margine non c'è e toglierla al calendario sarebbe
 * pagare altezza con larghezza.
 *
 * 🔤 I gruppi non sono decorazione: quindici voci in fila non hanno un ordine da
 * scorrere con l'occhio, e «Chiusure» la si trova leggendole tutte.
 */
/**
 * 🔽 **`chiudibile`: il capitolo si apre e si chiude con una freccetta** —
 * 26/09/2026, dal mockup approvato (`menu-a-capitoli-chiudibili-mockup.html`).
 * 🗣️ *«il menu laterale sta diventando abbastanza imponente»* · *«il lavoro non
 * me lo contrai»*. Le regole stanno in `features/menu/capitoli.ts`.
 */
interface Capitolo {
  nome: string;
  chiudibile?: true;
  voci: Voce[];
}

const GRUPPI: Capitolo[] = [
  {
    // 🏷️ «lavoro» senza l'articolo — 26/09/2026, richiesta sua, come «impostazioni».
    nome: 'lavoro',
    voci: [
      // 🔔 Voce 49: il pallino dice quante partite con l'acconto aspettano «Si è giocata» o «Campo vuoto».
      { dove: '/calendario', testo: 'Calendario', permesso: 'view_dashboard', conta: 'da-segnare' },
      { dove: '/soci', testo: 'Soci', permesso: 'view_members' },
      // 💶 Sta subito dopo i soci: è una schermata che si apre ogni sera, non
      // una che si configura una volta.
      // 💶 Dal 24/09/2026 (voce 23) dentro c'è anche il conto coi maestri, come
      // seconda linguetta: stesso permesso, stessa persona, stessa sera.
      { dove: '/cassa', testo: 'Cassa', permesso: 'view_incassi' },
    ],
  },
  {
    // ⭐ Il circolo descritto da sé: campi, orari, prezzi e maestri. È la parte
    // che rende il gestionale autonomo il giorno in cui Matchpoint si spegne.
    // 🏷️ «impostazioni» senza l'articolo — 26/09/2026, richiesta sua.
    nome: 'impostazioni',
    chiudibile: true,
    voci: [
      { dove: '/impostazioni/campi', testo: 'Campi', permesso: 'manage_settings' },
      { dove: '/impostazioni/fasce', testo: 'Fasce orarie', permesso: 'manage_settings' },
      { dove: '/impostazioni/chiusure', testo: 'Chiusure', permesso: 'manage_settings' },
      { dove: '/impostazioni/listino', testo: 'Listino prezzi giocatori', permesso: 'manage_settings' },
      { dove: '/impostazioni/maestri', testo: 'Maestri', permesso: 'manage_settings' },
      /**
       * 🗣️⭐ **«Listino prezzi maestri», nome scelto da lui il 20/09/2026** —
       * insieme a «Listino prezzi **giocatori**» per quello qui sopra.
       *
       * ⚖️ **I due nomi vanno letti come una coppia, ed è il punto.** Prima
       * erano *«Listino prezzi»* e *«Il campo ai maestri»*: due nomi di forma
       * diversa per due cose della stessa famiglia, e il secondo non diceva
       * di essere un listino. ⇒ Adesso la differenza sta nella parola che
       * cambia — **chi paga** — invece che nella forma della frase.
       * 📌 *Due voci che si distinguono per una parola sola si confrontano
       *    leggendole; due che si distinguono per la forma si confrontano
       *    ricordandosele.*
       *
       * ⛔ E resta ATTACCATA ai maestri e non ai prezzi: il listino dei
       * giocatori dice quanto paga un socio, questo quanto costa il campo a
       * chi ci fa lezione. **Due libri** — e il nome nuovo li avvicina senza
       * unirli.
       */
      {
        dove: '/impostazioni/campo-ai-maestri',
        testo: 'Listino prezzi maestri',
        permesso: 'manage_settings',
      },
      /**
       * 🎓 **«Listino prezzi lezioni»** — voce 19, tappa 19b (24/09/2026). Sta
       * dove stavano i «Pacchetti di lezioni», che sono stati tolti: 🗣️ *«io
       * ho bisogno, anziché inserire le persone che pagano, di inserire le
       * tariffe dei tipi di lezione»*. Ed è il terzo della famiglia: giocatori
       * (il campo a un socio), maestri (il campo a un maestro), lezioni (un
       * allievo a una lezione).
       */
      { dove: '/impostazioni/listino-lezioni', testo: 'Listino prezzi lezioni', permesso: 'manage_settings' },
      /**
       * 💳 **«Borsellino, Acconto e Crediti»** — voce 36, 27/09/2026, mockup
       * approvato. 🗣️ *«dobbiamo accorpare in una voce unica Borsellino, crediti,
       * acconto. E premio per chi organizza. Se no ci perdiamo.»* Prende il posto
       * di «Acconto» e «Premio a chi organizza»; dentro, quattro linguette.
       */
      {
        dove: '/impostazioni/borsellino',
        testo: 'Borsellino, Acconto e Crediti',
        permesso: 'manage_settings',
      },
    ],
  },
  {
    /**
     * 📱 **Il capitolo «Telegram»** — 24/09/2026, dal mockup approvato. 🗣️
     * *«Crea sul gestionale una sezione dedicata solamente a Telegram»*: le
     * quattro pagine che riguardano il bot stavano in due capitoli diversi, e
     * chi cercava una cosa del bot doveva sapere se era un'impostazione o un
     * accesso. Le pagine e i loro indirizzi non cambiano: cambia dove stanno.
     */
    nome: 'telegram',
    chiudibile: true,
    voci: [
      /**
       * 🤖 **«Cosa dice il bot ai soci»**, e non più «Bot dei soci»: 🗣️ *«trovagli
       * un nome più esplicativo di quello che c'è dentro»*. Dentro ci sono le
       * informazioni che il bot dà ai soci — segreteria, regole, il circolo —
       * e il nome vecchio diceva di chi era la pagina, non cosa ci si scrive.
       */
      { dove: '/impostazioni/bot-soci', testo: 'Cosa dice il bot ai soci', permesso: 'manage_settings' },
      /**
       * 📄 Voce 37 (27/09/2026, mockup approvato «37 AAA»): il regolamento
       * dell'acconto e l'informativa privacy, scritti qui e pubblicati a un
       * indirizzo nascosto che il bot manda ai soci.
       */
      { dove: '/impostazioni/documenti', testo: 'Documenti del bot', permesso: 'manage_settings' },
      { dove: '/impostazioni/rubrica', testo: 'Rubrica Telegram', permesso: 'view_admin_telegram' },
      /**
       * 💡 **«Segnalazioni dei soci»**, al posto di «Avvisi» — 24/09/2026, dal
       * mockup approvato: 🗣️ *«è importante ricevere dei suggerimenti dalle
       * persone, si impara sempre molto»*. Col numero di quelli da leggere.
       * 🏷️ Il 25/09 da «Suggerimenti» a «Segnalazioni dei soci» (mockup
       * `menu-suggerimenti-problemi-mockup.html`, scelta B): dentro ci sono
       * anche i problemi, e il nome diceva solo metà.
       * 🤖 Il 26/09 solo «Segnalazioni» (mockup dell'anomalia del prezzo): ci
       * scrive anche l'assistente digitale, non più soltanto i soci.
       */
      {
        dove: '/impostazioni/suggerimenti',
        testo: 'Segnalazioni',
        permesso: 'view_admin_telegram',
        conta: 'suggerimenti',
      },
      /**
       * 🔔 **«Avvisi ai soci»** torna nel menu — 25/09/2026, dal mockup
       * approvato (`avvisi-ai-soci-operativa-mockup.html`): 🗣️ *«rimettila nel
       * menu»*. Dal 24/09 ci si arrivava solo dal fondo della Rubrica, e per
       * sapere perché una risposta non era arrivata bisognava saperlo.
       * Col bollino dei non consegnati di oggi.
       */
      {
        dove: '/impostazioni/avvisi',
        testo: 'Avvisi ai soci',
        permesso: 'view_admin_notifiche',
        conta: 'avvisi',
      },
      /**
       * 🔑 **«Chiavi del ponte»** — voce 20 (24/09/2026). È la chiave con cui il
       * bot dei soci entra nel gestionale, quindi sta col bot (fino al
       * 24/09 stava fra «chi entra», perché è un accesso). Stesso permesso
       * degli utenti staff per guardarla, e `manage_users` per crearla o
       * revocarla (⇒ `chiavi.routes.ts`).
       */
      { dove: '/impostazioni/chiavi', testo: 'Chiavi del ponte', permesso: 'view_admin_utenti' },
    ],
  },
  {
    nome: 'chi entra',
    chiudibile: true,
    voci: [
      { dove: '/impostazioni/utenti', testo: 'Utenti staff', permesso: 'view_admin_utenti' },
      { dove: '/impostazioni/registro', testo: 'Registro', permesso: 'view_administration' },
      /**
       * ⭐ «Il mio account», non «La mia password» — 17/09/2026, richiesta sua.
       *
       * 🗣️ *«potrebbe diventare il mio account, che lì un po' gestisci tutto
       * l'account»* ⇒ la password è UNA delle cose del proprio accesso, e
       * intitolare la voce con quella nascondeva le altre: con quale email si è
       * entrati, con che ruolo, e cosa quel ruolo apre.
       */
      { dove: '/io/account', testo: 'Il mio account' },
    ],
  },
];

export function App(): React.JSX.Element {
  const { profilo, pronta } = useSessione();
  // 🎾 Serve al proprietario che insegna: è l'indirizzo a dire quale delle due
  //    applicazioni sta chiedendo. Sta qui in cima perché un hook non può stare
  //    dentro un ramo — e i rami, qui sotto, sono parecchi.
  const dove = useLocation();

  // Finché non si sa se c'è una sessione da riprendere dal cookie, non si
  // mostra né il gestionale né la schermata di accesso: mostrarne una delle due
  // vorrebbe dire far lampeggiare l'accesso a chi è già dentro.
  if (!pronta)
    return (
      <main className="accesso">
        <p className="attesa">Un attimo…</p>
      </main>
    );

  // 🚨 `/riapri` è l'unico indirizzo che deve funzionare SENZA essere entrati:
  // ci si arriva dal collegamento della posta, cioè proprio quando non si
  // riesce a entrare. Senza questa riga il collegamento porterebbe alla
  // schermata di accesso — che è esattamente la schermata da cui la persona
  // sta scappando.
  if (!profilo)
    return (
      <Routes>
        <Route path="/riapri" element={<Riapri />} />
        <Route path="*" element={<Accesso />} />
      </Routes>
    );

  // 🚨 Chi deve cambiare la password non va da nessun'altra parte — e non è una
  // cortesia dell'interfaccia: il server rifiuta tutto il resto con
  // `PASSWORD_DA_CAMBIARE`, quindi mostrargli un menu sarebbe mostrargli
  // bottoni che danno errore.
  if (profilo.mustChangePassword) {
    return (
      <Guscio gruppi={[]}>
        <CambioPassword obbligato />
      </Guscio>
    );
  }

  /**
   * 🎾⭐⭐ IL MAESTRO APRE UN'ALTRA APPLICAZIONE, non un menu più corto.
   *
   * ⚖️ Il ramo sta qui — **prima** che si calcolino i gruppi — perché il
   * maestro non ha nessun permesso da filtrare: `puo()` gli risponde no a tutto
   * (⇒ `shared/permessi.ts`), quindi `gruppi` sarebbe vuoto e `prima` cadrebbe
   * su `/io/account`. Con un guscio suo non c'è niente da far cadere.
   *
   * 🚨 E si guarda il **RUOLO**, non `profilo.maestro`: un proprietario che
   * insegna ha tutt'e due, e deve continuare a vedere il gestionale.
   * 📌 *Chi può entrare e cosa gli si mostra sono due domande diverse: la prima
   *    la decide un legame, la seconda un ruolo.*
   *
   * ✅ **E IL PROPRIETARIO CHE INSEGNA ADESSO CI ARRIVA — qui c'era scritto di
   *    no, ed era vero fino alla mattina del 18/09/2026.** Quella riga diceva
   *    che da questo guscio non esisteva **nessuna strada** verso `/maestro`:
   *    misurata, giusta, e curata il giorno stesso con la tappa ③ — la voce «La
   *    mia agenda» nel menu, e il ramo qui sotto. ⇒ La riga vecchia si
   *    **corregge**, non si affianca, o resterebbe a mandare in cerca di una
   *    mancanza che non c'è più.
   */
  if (profilo.role === 'maestro') return <AreaMaestro />;

  /**
   * 🎾⭐⭐ **E LA STRADA PER IL PROPRIETARIO CHE INSEGNA, che fino al 18/09/2026
   * NON ESISTEVA.**
   *
   * 📏 Era misurato e scritto qui sopra: il server la porta gliela apre (la
   * chiave è il **legame**, non il ruolo — ⇒ `plugins/autenticazione.ts`), ma da
   * questo guscio non c'era nessun indirizzo che ci arrivasse. ⇒ Adesso c'è, ed
   * è lo stesso: `/maestro`.
   *
   * ⚖️ **Un ramo e non una rotta dentro il guscio**, ed è la stessa ragione per
   * cui l'area maestro esiste: montarla dentro `Guscio` le metterebbe accanto la
   * barra laterale della segreteria — cioè proprio la cosa che su un telefono
   * copre la pagina. Sono due applicazioni, e ci si passa in mezzo con un
   * collegamento per parte (⇒ `AreaMaestro.tsx`, «Gestionale»).
   *
   * 🔒 La condizione è il **legame** (`profilo.maestro`), non un permesso: chi
   * non ha una scheda maestro attiva non ci arriva nemmeno scrivendo
   * l'indirizzo a mano — e dal server prenderebbe comunque 403.
   */
  if (profilo.maestro && dove.pathname.startsWith('/maestro')) return <AreaMaestro />;

  /**
   * ⚠️ Un gruppo che resta senza voci **non si mostra vuoto**: un'intestazione
   * «impostazioni» sopra il nulla fa credere che manchi qualcosa, mentre la
   * verità è che quella persona non ci arriva.
   */
  const gruppi: Capitolo[] = GRUPPI.map((g) => ({
    ...g,
    voci: g.voci.filter((v) => v.permesso === undefined || profilo.permissions.includes(v.permesso)),
  })).filter((g) => g.voci.length > 0);

  /**
   * 🎾 «La mia agenda» compare a chi **è anche un maestro**, e a nessun altro.
   *
   * ⚠️ Si aggiunge qui e non in `GRUPPI` perché non dipende da un permesso ma da
   * un legame: `GRUPPI` è una tabella di permessi, e infilarci una voce che si
   * filtra in un altro modo la renderebbe una tabella di due cose.
   */
  if (profilo.maestro) {
    const lavoro = gruppi.find((g) => g.nome === 'lavoro');
    const voce = { dove: '/maestro', testo: 'La mia agenda' };
    if (lavoro) lavoro.voci = [...lavoro.voci, voce];
    else gruppi.unshift({ nome: 'lavoro', voci: [voce] });
  }

  const prima = gruppi[0]?.voci[0]?.dove ?? '/io/account';

  return (
    <Guscio gruppi={gruppi}>
      <Routes>
        <Route path="/" element={<Navigate to={prima} replace />} />
        <Route
          path="/calendario"
          element={
            <RichiediPermesso nome="view_dashboard">
              <Calendario />
            </RichiediPermesso>
          }
        />
        <Route
          path="/soci"
          element={
            <RichiediPermesso nome="view_members">
              <Soci />
            </RichiediPermesso>
          }
        />
        <Route
          path="/cassa"
          element={
            <RichiediPermesso nome="view_incassi">
              <Cassa linguetta="incassi" />
            </RichiediPermesso>
          }
        />
        <Route
          path="/cassa/maestri"
          element={
            <RichiediPermesso nome="view_incassi">
              <Cassa linguetta="maestri" />
            </RichiediPermesso>
          }
        />
        <Route
          path="/cassa/caparre"
          element={
            <RichiediPermesso nome="view_incassi">
              <Cassa linguetta="caparre" />
            </RichiediPermesso>
          }
        />
        {/* Il vecchio indirizzo del conto coi maestri porta alla sua linguetta. */}
        <Route path="/conto-maestri" element={<Navigate to="/cassa/maestri" replace />} />
        <Route
          path="/impostazioni/utenti"
          element={
            <RichiediPermesso nome="view_admin_utenti">
              <Utenti />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/chiavi"
          element={
            <RichiediPermesso nome="view_admin_utenti">
              <Chiavi />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/campi"
          element={
            <RichiediPermesso nome="manage_settings">
              <Campi />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/fasce"
          element={
            <RichiediPermesso nome="manage_settings">
              <Fasce />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/chiusure"
          element={
            <RichiediPermesso nome="manage_settings">
              <Chiusure />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/listino"
          element={
            <RichiediPermesso nome="manage_settings">
              <Listini key="giocatori" />
            </RichiediPermesso>
          }
        />
        {/*
          ⚠️ Le RIGHE di prima restano raggiungibili, ma NON nel menu: danno i
          prezzi solo finché nessun listino copre quel giorno (⇒ `proponiImporto`),
          e una voce di menu le farebbe sembrare una seconda strada normale.
          Ci si arriva dal fondo del listino, dove è scritto anche perché.
        */}
        <Route
          path="/impostazioni/listino-righe"
          element={
            <RichiediPermesso nome="manage_settings">
              <Listino />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/maestri"
          element={
            <RichiediPermesso nome="manage_settings">
              <Maestri />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/campo-ai-maestri"
          element={
            <RichiediPermesso nome="manage_settings">
              {/* 💶 La stessa schermata dei giocatori (voce 19, tappa 19a): la
                  chiave la rifà da capo passando dall'una all'altra, così il
                  listino scelto di là non resta scelto di qua. */}
              <Listini key="maestri" per="maestri" />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/listino-lezioni"
          element={
            <RichiediPermesso nome="manage_settings">
              <ListinoLezioni />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/suggerimenti"
          element={
            <RichiediPermesso nome="view_admin_telegram">
              <Suggerimenti />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/avvisi"
          element={
            <RichiediPermesso nome="view_admin_notifiche">
              <Avvisi />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/rubrica"
          element={
            <RichiediPermesso nome="view_admin_telegram">
              <Rubrica />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/borsellino"
          element={
            <RichiediPermesso nome="manage_settings">
              <BorsellinoAccontoCrediti linguetta="borsellino" />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/borsellino/acconto"
          element={
            <RichiediPermesso nome="manage_settings">
              <BorsellinoAccontoCrediti linguetta="acconto" />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/borsellino/crediti"
          element={
            <RichiediPermesso nome="manage_settings">
              <BorsellinoAccontoCrediti linguetta="crediti" />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/borsellino/premio"
          element={
            <RichiediPermesso nome="manage_settings">
              <BorsellinoAccontoCrediti linguetta="premio" />
            </RichiediPermesso>
          }
        />
        {/* 🔄 Voce 36: gli indirizzi di prima portano alla linguetta giusta. */}
        <Route
          path="/impostazioni/caparra"
          element={<Navigate to="/impostazioni/borsellino/acconto" replace />}
        />
        <Route
          path="/impostazioni/premio"
          element={<Navigate to="/impostazioni/borsellino/premio" replace />}
        />
        <Route
          path="/impostazioni/bot-soci"
          element={
            <RichiediPermesso nome="manage_settings">
              <BotSoci />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/documenti"
          element={
            <RichiediPermesso nome="manage_settings">
              <Documenti />
            </RichiediPermesso>
          }
        />
        <Route
          path="/impostazioni/registro"
          element={
            <RichiediPermesso nome="view_administration">
              <Registro />
            </RichiediPermesso>
          }
        />
        <Route path="/io/account" element={<MioAccount />} />
        {/* ⚖️ L'indirizzo vecchio non si cancella, si rimanda: chi se l'era
              segnato fra i preferiti troverebbe altrimenti la pagina «questa
              pagina non c'è» al posto della sua password. */}
        <Route path="/io/password" element={<Navigate to="/io/account" replace />} />
        {/* ⚖️ Anche da dentro: se il collegamento si apre in un browser dove
              c'è già una sessione, questa pagina è quella che la persona si
              aspetta. E finita la riapertura la sessione cade da sé, perché il
              server le chiude tutte. */}
        <Route path="/riapri" element={<Riapri />} />
        {/* Un indirizzo che non esiste non è una pagina bianca. */}
        <Route
          path="*"
          element={
            <section className="riquadro vuoto">
              <h2>Questa pagina non c’è</h2>
              <p className="tenue">L’indirizzo non corrisponde a nessuna schermata.</p>
            </section>
          }
        />
      </Routes>
    </Guscio>
  );
}

/**
 * ⭐⭐ IL GUSCIO: la barra a sinistra, e la pagina accanto.
 *
 * 📏 **Perché a sinistra, in numeri** (⇒ il commento sopra `GRUPPI`): in alto
 * costava 128 px di altezza, che sul calendario sono quattro ore di giornata.
 * Un portatile è largo ma **corto** — 1440×900 lascia ~790 px di finestra, un
 * tablet in orizzontale ~700 — e sono proprio gli aggeggi su cui la segreteria
 * lavora.
 *
 * 🚨 **Sotto i 1200 px la barra si CHIUDE**, e non è una comodità: lì la
 * larghezza sprecata non c'è (le pagine erano già a 72rem, e un tablet è largo
 * 1180), quindi tenerla aperta pagherebbe l'altezza guadagnata con la
 * larghezza delle colonne del calendario. La regola vive nel CSS, che è l'unico
 * posto che conosce davvero la larghezza della finestra; qui c'è solo lo stato
 * «aperta/chiusa», che sopra i 1200 px non guarda nessuno.
 * 📌 *Una barra di icone indovinate sarebbe stata peggio: «Chiusure»,
 *    «Rubrica» e «Utenti staff» non hanno un disegnino che si capisca.*
 */
function Guscio({ gruppi, children }: { gruppi: Capitolo[]; children: React.ReactNode }): React.JSX.Element {
  const { profilo } = useSessione();
  const versione = usaVersione();
  const daLeggere = usaDaLeggere(gruppi.some((g) => g.voci.some((v) => v.conta === 'suggerimenti')));
  const nonConsegnati = usaNonConsegnatiOggi(gruppi.some((g) => g.voci.some((v) => v.conta === 'avvisi')));
  const daSegnare = usaDaSegnare(gruppi.some((g) => g.voci.some((v) => v.conta === 'da-segnare')));
  const [aperta, setAperta] = useState(false);
  const dove = useLocation().pathname;
  /**
   * 🔽 All'apertura del gestionale i capitoli partono CHIUSI, sempre: 🗣️
   * *«quando apro il gestionale deve essere chiusa di default»* (26/09/2026).
   * Non si ricordano fra un'apertura e l'altra (⇒ `features/menu/capitoli.ts`).
   */
  const [capitoliAperti, setCapitoliAperti] = useState<ReadonlySet<string>>(() => new Set());
  const conte = { suggerimenti: daLeggere, avvisi: nonConsegnati, 'da-segnare': daSegnare };
  const paginaDiPrima = useRef(dove);

  function giraCapitolo(nome: string): void {
    const nuovi = new Set(capitoliAperti);
    if (nuovi.has(nome)) nuovi.delete(nome);
    else nuovi.add(nome);
    setCapitoliAperti(nuovi);
  }

  /**
   * 📍 Cambiando pagina si apre il suo capitolo. Solo al CAMBIO — non
   * all'apertura, che parte chiusa — e chi lo chiude stando lì dentro lo
   * ritrova chiuso finché non va su un'altra pagina.
   */
  useEffect(() => {
    if (paginaDiPrima.current === dove) return;
    paginaDiPrima.current = dove;
    setCapitoliAperti((prima) => apriIlCapitoloDellaPagina(prima, gruppi, dove));
  }, [dove, gruppi]);

  /**
   * ⚠️ Cambiando schermata la barra si richiude DA SÉ.
   *
   * Sul telefono copre la pagina: lasciarla aperta dopo un click vorrebbe dire
   * aprire una schermata e non vederla — l'utente crederebbe che il
   * collegamento non ha funzionato, e ripremerebbe.
   */
  useEffect(() => setAperta(false), [dove]);

  return (
    <div className={`guscio${aperta ? ' menu-aperto' : ''}`}>
      {/* Chiude toccando fuori: su un telefono è il gesto che si prova per primo. */}
      <button
        type="button"
        className="velo-menu"
        aria-label="Chiudi il menu"
        tabIndex={aperta ? 0 : -1}
        onClick={() => setAperta(false)}
      />

      <aside className="barra-laterale">
        {/* ⭐⭐ SOTTO IL NOME CI STA LA VERSIONE, NON L'ACCOUNT — 17/09/2026,
            richiesta sua: *«vorrei sotto il nome Padel Village il numero di
            versione, e poi vorrei che si levasse la mail più la scritta
            owner»*.
            ⚖️ Email e ruolo non sono spariti: sono scesi in «Il mio account»,
            perché sono due cose che si guardano quando ci si chiede *«con quale
            accesso sono dentro?»* — cioè di rado — e stavano sotto gli occhi
            tutto il giorno.
            📏 E il numero non è scritto qui: arriva da `/api/v1/health`, cioè
            dal tag che ha costruito l'immagine ⇒ dice la versione VIVA, non
            quella che qualcuno si è ricordato di aggiornare a mano. */}
        <div className="chi-sono">
          <strong>Padel Village</strong>
          {/* Finché il server non ha risposto non si scrive niente: un trattino
              al posto del numero sembra un guasto, e dura mezzo secondo. */}
          {versione !== null && <span className="numero-versione">{versione}</span>}
          {/* 🚨 UN `readonly` DEVE SAPERLO, e prima di premere «Salva».
              📏 Trovato il 16/09/2026 col banco della Fase 10: le schermate già
              nascondono i «Salva» che sanno di non potergli servire, ma il
              calendario no — e in testata c'era solo la parola `readonly`
              accanto all'email: un codice, non una frase.
              📌 *Una facoltà che manca si dice in italiano, o si scopre
                 premendo.*
              ⚖️ Resta qui anche adesso che l'account è sceso in fondo: è
              l'unica riga della barra che cambia cosa si può FARE, e in una
              schermata che si apre una volta al mese non la vedrebbe nessuno. */}
          {profilo?.role === 'readonly' && (
            <span className="segno-sola-lettura" title="Il server rifiuta ogni scrittura da questo accesso">
              Sola lettura
            </span>
          )}
        </div>

        <nav className="menu">
          {gruppi.map((g) => {
            const visibile = capitoloAperto(g, capitoliAperti);
            const bollino = visibile ? 0 : bollinoDelCapitolo(g.voci, conte);
            return (
              <div key={g.nome} className="gruppo-menu">
                {g.chiudibile ? (
                  <button
                    type="button"
                    className="titolo-gruppo apri-capitolo"
                    aria-expanded={visibile}
                    onClick={() => giraCapitolo(g.nome)}
                  >
                    {g.nome}
                    {bollino > 0 && (
                      <span className="da-leggere" aria-label={`${bollino} da guardare`}>
                        {bollino}
                      </span>
                    )}
                    <span className="freccia-capitolo" aria-hidden="true">
                      ▸
                    </span>
                  </button>
                ) : (
                  <span className="titolo-gruppo">{g.nome}</span>
                )}
                {visibile &&
                  g.voci.map((v) => (
                    <NavLink
                      key={v.dove}
                      to={v.dove}
                      className={({ isActive }) => (isActive ? 'attivo' : '')}
                    >
                      {v.testo}
                      {v.conta === 'suggerimenti' && daLeggere > 0 && (
                        <span className="da-leggere" aria-label={`${daLeggere} da leggere`}>
                          {daLeggere}
                        </span>
                      )}
                      {v.conta === 'da-segnare' && daSegnare > 0 && (
                        <span className="da-leggere" aria-label={`${daSegnare} partite da segnare`}>
                          {daSegnare}
                        </span>
                      )}
                      {v.conta === 'avvisi' && nonConsegnati > 0 && (
                        <span className="da-leggere" aria-label={`${nonConsegnati} non consegnati oggi`}>
                          {nonConsegnati}
                        </span>
                      )}
                    </NavLink>
                  ))}
              </div>
            );
          })}
        </nav>

        <button type="button" className="piatto esci" onClick={() => void esci()}>
          Esci
        </button>
      </aside>

      <div className="lato-pagina">
        {/* Solo quando la barra è chiusa dal CSS: sopra i 1200 px sparisce. */}
        <button
          type="button"
          className="apri-menu"
          aria-expanded={aperta}
          onClick={() => setAperta((v) => !v)}
        >
          ☰ Menu
        </button>
        <main className="pagina">{children}</main>
      </div>
    </div>
  );
}
